/**
 * The cart, as the shape the pricing endpoints take.
 *
 * `/quotes/options` and `/quotes/bindings` answer "what would the ORDER cost
 * if this one file changed?", so they need the rest of the cart held fixed in
 * an `others` array. This turns one into the other.
 *
 * ONLY PRICEABLE ITEMS CROSS OVER. Those endpoints resolve every document
 * they are given and refuse the whole request if one is still being checked
 * or cannot be printed — which is the difference between a cart and a basket,
 * and the reason the filter lives here rather than being remembered at each
 * call site.
 */
import type { CartItemOut, CartOut, DocumentRequest } from '@/lib/api/types';

function toDocument(item: CartItemOut): DocumentRequest {
  return {
    upload_id: item.upload_id,
    // Slugs, not ids: a slug survives a catalogue row being replaced, and it
    // is readable in a request log.
    ...(item.print_option ? { print_option_slug: item.print_option.slug } : {}),
    ...(item.binding ? { binding_slug: item.binding.slug } : {}),
    ...(item.cover_colour ? { cover_colour: item.cover_colour } : {}),
    copies: item.copies,
  };
}

/** Every file in the cart that the pricing endpoints will accept. */
export function documentsFromCart(cart: CartOut | undefined): DocumentRequest[] {
  if (!cart) return [];
  return cart.items.filter((item) => item.priceable).map(toDocument);
}

/**
 * The cart except the file being configured right now.
 *
 * Matched on `upload_id` rather than position, because position moves when
 * something above it is removed and the file being configured does not.
 */
export function otherCartDocuments(
  cart: CartOut | undefined,
  uploadId: string | undefined,
): DocumentRequest[] {
  return documentsFromCart(cart).filter((document) => document.upload_id !== uploadId);
}
